import React from "react";
import styled from "styled-components";
import {
  Box,
  Card,
  Divider,
  Status,
  Text,
  TrackedLink,
} from "@origin-digital/ods-core";
import { IconDescription } from "@origin-digital/ods-icons";
import type { ISearchResultDto } from "../../contract/generated/bff/src/models";
import { trackClick } from "../../helpers/tracking";

const RecommendedBadge = styled.span`
  position: absolute;
  top: -14px;
  left: 24px;
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.info};
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.white};
`;

const RecommendedCard = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.palette.backgroundColor.info.light};
  border-radius: 12px;
  padding: 28px 16px 16px;
`;

const SummaryText = styled.p<{ $compact?: boolean }>`
  margin: 0;
  font-size: ${({ $compact }) => ($compact ? "12px" : "14px")};
  font-weight: 400;
  color: ${(props) => props.theme.colors.neutral};

  ${({ $compact }) =>
    $compact
      ? `
    line-height: 1.5;
    max-height: calc(1.5em * 2);
    overflow: hidden;
  `
      : `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}

  ${({ theme, $compact }) =>
    !$compact &&
    `
    ${theme.breakpoints.down("md")} {
      -webkit-line-clamp: 5;
    }
  `}
`;

const CategoryButton = styled(Status)<{ $isRecommended: boolean }>`
  padding: 8px 12px;
  border-radius: 16px;
  background-color: ${({ $isRecommended, theme }) =>
    $isRecommended ? theme.colors.white : null} !important;
`;

const CardLink = styled(TrackedLink)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

const TitleText = styled.span`
  ${CardLink}:hover & {
    text-decoration: underline;
  }
  font-weight: 600;
`;

const IconSpacer = styled.div`
  visibility: hidden;
  flex-shrink: 0;
`;

const PillLink = styled.a`
  text-decoration: none;
  color: inherit;
`;

interface SearchResultItemProps {
  result: ISearchResultDto;
}

const CompactResultLayout: React.FC<{
  result: ISearchResultDto;
  renderedTitle?: React.ReactNode;
}> = ({ result, renderedTitle }) => (
  <Box display="flex" flexDirection="row" alignItems="flex-start" gap="small">
    <Box>
      <IconDescription size="xxsmall" color="secondaryB" />
    </Box>
    <Box width="full">
      <Box marginBottom="xsmall">
        <Text tone="neutralDark" weight="medium" variant="body">
          <TitleText>{renderedTitle ?? result.title}</TitleText>
        </Text>
      </Box>
      <SummaryText $compact>{result.summary}</SummaryText>
    </Box>
  </Box>
);

const TabCategoryLabel: React.FC<{ category: string }> = ({ category }) => (
  <Box marginBottom="xxsmall">
    <Text variant="body-small" tone="neutralLight" weight="medium">
      {category}
    </Text>
  </Box>
);

const ArticleCategoryPill: React.FC<{
  articleCategory: string;
  articleCategoryUrl?: string | null;
  isRecommended: boolean;
  compact?: boolean;
}> = ({ articleCategory, articleCategoryUrl, isRecommended, compact }) => (
  <Box marginTop={compact ? "xxsmall" : "small"}>
    <PillLink
      href={articleCategoryUrl ?? undefined}
      onClick={(e) => e.stopPropagation()}
    >
      <CategoryButton
        $isRecommended={isRecommended}
        weight="regular"
        tone="neutral"
        title={articleCategory}
        data-id={`article-category-${articleCategory}`}
      >
        {articleCategory}
      </CategoryButton>
    </PillLink>
  </Box>
);

export const CardContent: React.FC<{
  result: ISearchResultDto;
  isRecommended: boolean;
  renderedTitle?: React.ReactNode;
  compact?: boolean;
}> = ({ result, isRecommended, renderedTitle, compact = false }) => {
  const isHelpArticle = result.url.includes("/help-support/");
  const showPill = compact
    ? !!result.articleCategory
    : isHelpArticle && !!result.articleCategory;

  const trackingLabel = isRecommended
    ? "recommended-search-result"
    : "search-result";
  const trackingType = isRecommended ? "RecommendedLink" : "ResultLink";

  return (
    <Box>
      <Card
        backgroundColor="transparent"
        outline={false}
        paddingXSpecific="small"
        paddingYSpecific="small"
      >
        <CardLink
          href={result.url}
          data-id={`search-result-tile-${result.rank}`}
          onClick={() => trackClick(result.clickTrackingUrl)}
          trackingLabel={trackingLabel}
          trackingType={trackingType}
        >
          {compact ? (
            <CompactResultLayout
              result={result}
              renderedTitle={renderedTitle}
            />
          ) : (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="flex-start"
              gap="small"
            >
              <Box>
                <IconDescription size="xxsmall" color="secondaryB" />
              </Box>
              <Box width="full">
                {result.tabCategory && (
                  <TabCategoryLabel category={result.tabCategory} />
                )}
                <Box marginBottom="xsmall">
                  <Text
                    tone="neutralDark"
                    weight="medium"
                    variant="subtitle-small"
                  >
                    <TitleText>{renderedTitle ?? result.title}</TitleText>
                  </Text>
                </Box>
                <SummaryText>{result.summary}</SummaryText>
              </Box>
            </Box>
          )}
        </CardLink>
      </Card>
      {showPill && (
        <Box display="flex" flexDirection="row" gap="small" paddingX="small">
          <IconSpacer>
            <IconDescription size="xxsmall" />
          </IconSpacer>
          <ArticleCategoryPill
            articleCategory={result.articleCategory!}
            articleCategoryUrl={result.articleCategoryUrl}
            isRecommended={isRecommended}
            compact={compact}
          />
        </Box>
      )}
    </Box>
  );
};

export const SearchResultItem: React.FC<SearchResultItemProps> = ({
  result,
}) => {
  const isRecommended = result.rank === 1;

  return (
    <Box data-id={`search-result-item-${result.rank}`}>
      <Box marginY="medium">
        {isRecommended ? (
          <RecommendedCard>
            <RecommendedBadge>Recommended</RecommendedBadge>
            <CardContent result={result} isRecommended={isRecommended} />
          </RecommendedCard>
        ) : (
          <Box>
            <Box paddingBottom="medium" paddingTop="xxsmall">
              <Divider />
            </Box>
            <CardContent result={result} isRecommended={isRecommended} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
